import { useRef, type KeyboardEvent, type MouseEvent, type RefObject } from 'react'

/**
 * Keyboard and pointer navigation over a table of rows.
 *
 * A teller works a queue without reaching for the mouse: type into the search box, arrow down
 * into the results, arrow through them, press Enter to open one. The rows themselves are already
 * focusable and already answer Enter — that belongs to the shared table — so what is added here
 * is the movement between them, the way in from the search box, and a single click opening a row
 * rather than a double one.
 *
 * The handlers sit on the container rather than on each row so that they keep working across a
 * refetch that replaces every row element underneath them.
 */

/** Marks the control that Arrow Down enters the table from, and that Arrow Up returns to. */
export const ROW_NAV_SOURCE_PROPS = { 'data-row-nav-source': 'true' } as const

const ROW_SELECTOR = 'tbody tr[tabindex="0"]'

const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, [role="menuitem"], [role="option"]'

export interface RowNavigation {
  containerRef: RefObject<HTMLDivElement | null>
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  onClick: (event: MouseEvent<HTMLDivElement>) => void
}

export interface RowNavigationOptions {
  /** Rows currently rendered. Movement is clamped to this, never wrapped. */
  rowCount: number
  /** Opening a row: the index is into the page currently on screen. */
  onActivate: (index: number) => void
}

export function useRowNavigation({ rowCount, onActivate }: RowNavigationOptions): RowNavigation {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const rowElements = () =>
    Array.from(containerRef.current?.querySelectorAll<HTMLTableRowElement>(ROW_SELECTOR) ?? [])

  const focusRow = (index: number) => {
    const rows = rowElements()
    const target = rows[Math.min(Math.max(index, 0), rows.length - 1)]
    target?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (rowCount === 0) {
      return
    }

    const target = event.target as HTMLElement
    const row = target.closest<HTMLTableRowElement>(ROW_SELECTOR)
    const fromSource = target.matches('[data-row-nav-source]')

    // Everything else on the toolbar — the filter selects, the paging buttons — keeps its own
    // arrow-key behaviour. Only the search box and the rows hand their keys over.
    if (!row && !fromSource) {
      return
    }

    const rows = rowElements()
    const index = row ? rows.indexOf(row) : -1

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusRow(index + 1)
        break

      case 'ArrowUp':
        if (index <= 0) {
          if (row) {
            event.preventDefault()
            containerRef.current?.querySelector<HTMLElement>('[data-row-nav-source]')?.focus()
          }
          break
        }

        event.preventDefault()
        focusRow(index - 1)
        break

      case 'Home':
        if (row) {
          event.preventDefault()
          focusRow(0)
        }
        break

      case 'End':
        if (row) {
          event.preventDefault()
          focusRow(rows.length - 1)
        }
        break

      default:
        break
    }
  }

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    if (rowCount === 0) {
      return
    }

    const target = event.target as HTMLElement
    if (target.closest(INTERACTIVE_SELECTOR)) {
      return
    }

    const row = target.closest<HTMLTableRowElement>(ROW_SELECTOR)
    if (!row) {
      return
    }

    const index = rowElements().indexOf(row)
    if (index >= 0 && index < rowCount) {
      onActivate(index)
    }
  }

  return { containerRef, onKeyDown, onClick }
}
